import { CountryMaster, ICompanyInformationDto, RequestDomain, RequestDto, RequestStatus, UserDto } from 'generated/request-backend';
import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from '@fluentui/react';
import { IconButton, TextField, Text, TooltipHost, DirectionalHint } from '@fluentui/react';
import { ISOToDateTime, formatCurrency } from 'app/components/helpers';
import { isSuperAdmin } from 'app/utils/authUtilities';
import useStore, { IStore } from 'app/store';
import './CustomerDetailsTab.scss';
import NumberFormat from 'react-number-format';
import { useId } from '@fluentui/react-hooks';
import { StringConstants } from 'app/utils/constants';
import { IRemoveCustomerImpactData } from 'app/models/common/request';
import { IMPORTANCE_REASON_LABELS } from 'app/modules/user/createTicket/customerImpact/types';
import { parseImpactDescription } from 'app/modules/user/createTicket/customerImpact/customerImpactMapping';
import { isGeneralFeedbackTicket } from 'app/modules/newTicketView/generalFeedback';
import GeneralFeedbackEmptyState from '../requestDetailsTab/GeneralFeedbackEmptyState';
import { getDescriptionText } from 'app/modules/user/addCustomerWizard/requestDetails/types';

const REASON_DESCRIPTION_BY_LABEL: Record<string, string> = Object.values(IMPORTANCE_REASON_LABELS).reduce(
    (acc, reason) => {
        acc[reason.label] = reason.description;
        return acc;
    },
    {} as Record<string, string>
);

/**
 * The per-customer "why important" reason is folded into the free-text
 * customerImpactDescription at create time as "Why important: <label>". Parse it
 * back out so the card can show the reason label plus its canned subtitle.
 */
const parseWhyImportant = (description?: string): { label: string; description: string } | null => {
    if (!description) {
        return null;
    }
    const parsed = parseImpactDescription(description);
    if (!parsed.importanceReason) {
        return null;
    }
    const label =
        parsed.importanceReason === 'other'
            ? parsed.importanceOtherText ?? ''
            : IMPORTANCE_REASON_LABELS[parsed.importanceReason].label;
    return { label, description: REASON_DESCRIPTION_BY_LABEL[label] ?? '' };
};

interface IProps {
    selectedRequest: RequestDto;
    toggleHideDialog: () => void;
    setImpactForRemoval: (impact: IRemoveCustomerImpactData) => void;
    isEditMode?: boolean;
    companyInformation?: ICompanyInformationDto;
    setCompanyInformation?: (info: ICompanyInformationDto) => void;
    customerDetailsValidation: { emailValidText: boolean; contactNumberValidText: boolean; alternateContactValidText: boolean };
    setCustomerDetailsValidation: ({ emailValidText, contactNumberValidText, alternateContactValidText }: { emailValidText: boolean; contactNumberValidText: boolean; alternateContactValidText: boolean; }) => void;
    countyCodePrefix: { contactNumber: string; alternateContactNumber: string };
    setCountryCodePrefix: ({ contactNumber, alternateContactNumber }: { contactNumber: string; alternateContactNumber: string }) => void;
    countryList: CountryMaster[];
    onCompletedHandler?: () => void;
    /** Opens the shared Add Customer modal. Passed from DetailsPanel; used by the card kebab "Edit" action. */
    onAddOrEditCustomer?: (item?: IRemoveCustomerImpactData) => void;
    /** Creator and Admin hosts can manage customer impacts; Viewer hosts render cards read-only. */
    canManageCustomers?: boolean;
    showCustomerCountHeader?: boolean;
}

const CustomerDetailsTab: React.FC<IProps> = (props) => {
    const {
        selectedRequest,
        companyInformation,
        customerDetailsValidation,
        setCustomerDetailsValidation,
        countyCodePrefix,
        setCountryCodePrefix
    } = props;
    const {
        toggleHideDialog,
        setImpactForRemoval,
        isEditMode,
        setCompanyInformation,
        onAddOrEditCustomer,
        canManageCustomers = false,
        showCustomerCountHeader = false
    } = props;
    const userDetails: UserDto = useStore((state: IStore) => state.userDetails) as UserDto;

    // For edit mode to retain values on data refetches
    const [isEditingCompanyInformation, setIsEditingCompanyInformation] = useState<boolean>(false);
    // const [companyAltPhoneNumberFieldValue, setCompanyAltPhoneNumberFieldValue] = useState<string>('');
    const [companyFullAddressFieldValue, setCompanyFullAddressFieldValue] = useState<string>('');
    const [expandedCards, setExpandedCards] = useState<Set<number>>(
        () => new Set((selectedRequest.customerImpacts || []).map((_impact, index) => index))
    );
    useEffect(() => {
        setExpandedCards(new Set((selectedRequest.customerImpacts || []).map((_impact, index) => index)));
    }, [selectedRequest.unifiedTrackingId, selectedRequest.customerImpacts]);
    const toggleCard = (index: number) => {
        setExpandedCards((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };
    const handleCardHeaderKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
        if ((event.target as HTMLElement).closest('a, button')) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleCard(index);
        }
    };

    // helper functions
    useEffect(()=> {
        // setCompanyAltPhoneNumberFieldValue(companyInformation.companyAltPhoneNumber);
        setCompanyFullAddressFieldValue(companyInformation.companyFullShippingAddress);
    }, [isEditMode]);

    const handleRemoveCustomerImpact = (item: IRemoveCustomerImpactData) => {
        toggleHideDialog();
        setImpactForRemoval(item);
    };

    const handleValueChange = (fieldName: string, newValue?: string) => {
        setIsEditingCompanyInformation(true);

        const fieldKey = fieldName as keyof ICompanyInformationDto;

        // if (fieldKey === 'companyName') {
        //     setCompanyNameFieldValue(newValue);
        // }
        // else if (fieldKey === 'companyContactName') {
        //     setCompanyContactNameFieldValue(newValue);
        // }
        // else if (fieldKey ==='contactEmailAddress') {
        //     setCompanyEmailAddressFieldValue(newValue);
        // }
        // else if (fieldKey === 'contactPhoneNumber') {
        //     setCompanyPhoneNumberFieldValue(newValue);
        // }
        // else if (fieldKey === 'alternatePhoneNumber') {
        //     setCompanyAltPhoneNumberFieldValue(newValue);
        // }
        // else if (fieldKey === 'companyFullShippingAddress') {
        if(fieldKey === 'companyFullShippingAddress') {
            setCompanyFullAddressFieldValue(newValue);
        }

        setCompanyInformation({...companyInformation, [fieldKey]: newValue });
    };
    const getData = (value: string, prefix: string) => {
        if (value && value.length > 2 && !value.includes(StringConstants.UNDEFINED)) {
            if (prefix && prefix !== StringConstants.UNDEFINED && prefix !== 'null') {
                return '+' + prefix + ' ' + value;
            } else {
                return value;
            }
        } else {
            return '--';
        }
    };
    const prepareOptions = (type: string) => {
        let options: Array<JSX.Element> = [];
        if (
            (type === StringConstants.CONTACT_NUM &&
                (!countyCodePrefix.contactNumber || countyCodePrefix.contactNumber?.includes(StringConstants.UNDEFINED))) ||
            (type === StringConstants.ALT_CONTACT_NUM && !countyCodePrefix.alternateContactNumber) ||
            countyCodePrefix.alternateContactNumber?.includes(StringConstants.UNDEFINED)
        ) {
            options.push(
                <option value={null} selected disabled hidden>
                    Select the country
                </option>
            );
        }

        if (props.countryList) {
            props.countryList.forEach((item: CountryMaster) => {
                options.push(
                    <option key={item.id} value={item.dialingCode}>
                        {item.name}
                        {' - +'}
                        {item.dialingCode}
                    </option>
                );
            });
        }
        return options;
    };
    const tooltipId = useId('tooltip');
    const getTooltip = (city?: string, state?: string) => {
        const res: string[] = [];
        if (city) {
            res.push(city);
        }
        if (state) {
            res.push(state);
        }
        return res.join(', ');
    };
    const urlString = "https://microsoftsales.crm.dynamics.com/main.aspx?appid=fe0c3504-3700-e911-a849-000d3a10b7cc&pagetype=entityrecord&etn=opportunity&id=";
    const handleLink = (opportunityId:string, backendOpportunityId: string) => {
        return (
            <Link target="_blank" href={urlString + backendOpportunityId}>
                {opportunityId}
            </Link>
    )}

    return (
        <>
            <div className="customer-details-cont full-width-cont">
                <div className=" row-flex-box full-width-cont">
                    <div className="col-flex full-width-cont">
                        {selectedRequest.isParent || !isEditMode ? (
                            <>
                                <div className="m-b-25" />
                            </>
                        ) : (
                            <>
                                <div className="row-flex-box full-width-cont">
                                    <div className="col-flex  half-width-cont p-r-40">
                                        <div className="row-flex-box m-b-25">
                                            <div className="label-cont">
                                                <Trans>Customer Name: </Trans>
                                            </div>

                                            <div className="field-cont">
                                                {companyInformation?.companyName ? (
                                                    <TooltipHost
                                                        className="tooltip-text"
                                                        content={companyInformation?.companyName}
                                                        id={tooltipId}>
                                                        <Text className="limit-text">
                                                            {!isEditMode && companyInformation?.companyName}
                                                        </Text>
                                                    </TooltipHost>
                                                ) : (
                                                    <Text> {!isEditMode && !companyInformation?.companyName && '--'}</Text>
                                                )}

                                                {isEditMode && (
                                                    <div className="desc-textfield-wrap ">
                                                        <TextField
                                                            value={companyInformation?.companyName}
                                                            placeholder="e.g: Contoso Corporation"
                                                            resizable={false}
                                                            borderless
                                                            onChange={(
                                                                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                                                                newValue?: string
                                                            ) => {
                                                                handleValueChange('companyName', newValue);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="row-flex-box m-b-25">
                                            <div className="label-cont">
                                                <Trans>Contact Name: </Trans>
                                            </div>
                                            <div className="field-cont">
                                                {!isEditMode && companyInformation?.companyContactName}
                                                {!isEditMode && !companyInformation?.companyContactName && '--'}
                                                {isEditMode && (
                                                    <div className="desc-textfield-wrap">
                                                        <TextField
                                                            value={companyInformation?.companyContactName}
                                                            placeholder="e.g: Alesia Hester"
                                                            resizable={false}
                                                            borderless
                                                            onChange={(
                                                                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                                                                newValue?: string
                                                            ) => {
                                                                handleValueChange('companyContactName', newValue);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-flex half-width-cont">
                                        <div className="row-flex-box m-b-25">
                                            <div className="label-cont">
                                                <Trans>Email ID: </Trans>
                                            </div>
                                            <div className="field-cont">
                                                {!isEditMode && companyInformation?.contactEmailAddress}
                                                {!isEditMode && !companyInformation?.contactEmailAddress && '--'}
                                                {isEditMode && (
                                                    <div
                                                        className={`desc-textfield-wrap ${
                                                            customerDetailsValidation.emailValidText ? 'error-field' : ''
                                                        }`}>
                                                        <TextField
                                                            value={companyInformation?.contactEmailAddress}
                                                            placeholder="e.g: jane.doe@microsoft.com"
                                                            resizable={false}
                                                            borderless
                                                            onChange={(
                                                                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                                                                newValue?: string
                                                            ) => {
                                                                setCustomerDetailsValidation({
                                                                    ...customerDetailsValidation,
                                                                    emailValidText: false
                                                                });
                                                                handleValueChange('contactEmailAddress', newValue);
                                                            }}
                                                        />
                                                        {customerDetailsValidation.emailValidText && (
                                                            <Text className="helper-txt">
                                                                <Trans>Please enter a valid ID</Trans>
                                                            </Text>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-flex m-b-25">
                                            <div className="row-flex-box">
                                                <div className="label-cont">
                                                    <Trans>Contact number: </Trans>
                                                </div>

                                                <div className="field-cont">
                                                    {!isEditMode &&
                                                        getData(
                                                            companyInformation?.contactPhoneNumber,
                                                            countyCodePrefix.contactNumber
                                                        )}

                                                    {isEditMode && (
                                                        <div className="custom-row col-flex">
                                                            <div
                                                                className={`row-flex ${
                                                                    customerDetailsValidation.contactNumberValidText
                                                                        ? 'error-field'
                                                                        : ''
                                                                }`}>
                                                                <div>
                                                                    <select
                                                                        value={countyCodePrefix.contactNumber}
                                                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                                            setCountryCodePrefix({
                                                                                ...countyCodePrefix,
                                                                                contactNumber: e.target.value
                                                                            });
                                                                            setCustomerDetailsValidation({
                                                                                ...customerDetailsValidation,
                                                                                contactNumberValidText: false
                                                                            });
                                                                        }}
                                                                        className="entry-width"
                                                                        aria-label="Enter customer mobile number">
                                                                        {prepareOptions(StringConstants.CONTACT_NUM)}
                                                                    </select>
                                                                </div>
                                                                <div>
                                                                    <NumberFormat
                                                                        mask=" "
                                                                        className="regular-text-field format-field"
                                                                        value={companyInformation.contactPhoneNumber}
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                            setCustomerDetailsValidation({
                                                                                ...customerDetailsValidation,
                                                                                contactNumberValidText: false
                                                                            });
                                                                            handleValueChange(
                                                                                'contactPhoneNumber',
                                                                                e.target.value
                                                                            );
                                                                        }}
                                                                        placeholder="e.g: 111 111 1111"
                                                                        allowNegative={false}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {customerDetailsValidation.contactNumberValidText && (
                                                <Text className="helper-txt">
                                                    <Trans>Please enter a valid contact number.</Trans>
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {selectedRequest && selectedRequest.requestTeam === RequestDomain.Crew && isEditMode && (
                            <div>
                                <div>
                                    <div className="desc-textfield-wrap m-b-20">
                                        <TextField
                                            value={isEditingCompanyInformation ? companyFullAddressFieldValue : companyInformation.companyFullShippingAddress}
                                            placeholder="e.g: 866 Summit Park Avenue, Southfield, Michigan, United States(48075)"
                                            multiline
                                            resizable={false}
                                            borderless
                                            rows={6}
                                            onChange={(
                                                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                                                newValue?: string
                                            ) => {
                                                handleValueChange('companyFullShippingAddress', newValue);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

                <div className="customer-impact-cards">
                    {showCustomerCountHeader && (
                        <div className="ci-list-heading">
                            Customers ({selectedRequest.customerImpacts?.length || 0})
                        </div>
                    )}
                    {selectedRequest?.customerImpacts &&
                        selectedRequest.customerImpacts.map((item, index) => {
                            const expanded = expandedCards.has(index);
                            const impactLabel =
                                item.opportunityId ||
                                item.msxOpportunityId ||
                                item.customerName ||
                                `customer ${index + 1}`;
                            const canManage = canManageCustomers || isSuperAdmin(userDetails?.userRoles);
                            const canRemove =
                                canManage &&
                                selectedRequest.isParent !== true &&
                                !selectedRequest.parentRequestId;
                            const canEdit =
                                selectedRequest.status !== RequestStatus.Resolved &&
                                selectedRequest.status !== RequestStatus.Closed;
                            return (
                                <div
                                    className={`ci-card${expanded ? ' expanded' : ''}`}
                                    data-testid="customer-impact-card"
                                    key={item.opportunityId || item.accountId || item.customerName || index}>
                                    <div
                                        className="ci-card-head"
                                        data-testid="customer-impact-card-header"
                                        onClick={() => toggleCard(index)}
                                        onKeyDown={(event) => handleCardHeaderKeyDown(event, index)}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={expanded}>
                                        <div className="ci-card-heading">
                                            <div className="ci-card-title">{item.customerName || '--'}</div>
                                            <div className="ci-card-summary">
                                                <Trans>Opportunity</Trans>:{' '}
                                                {item.opportunityId
                                                    ? handleLink(item.opportunityId, item.msxOpportunityId)
                                                    : '--'}
                                                <span className="ci-sep">|</span>
                                                <Trans>Revenue Impacted</Trans>: $ {formatCurrency(item.msxRevenue)}
                                                <span className="ci-sep">|</span>
                                                <Trans>Devices Impacted</Trans>: {formatCurrency(item.msxDeviceCount)}
                                            </div>
                                        </div>
                                        <div className="ci-card-actions">
                                            {canRemove && (
                                                <IconButton
                                                    className="ci-card-kebab"
                                                    data-testid="customer-actions-button"
                                                    role="button"
                                                    ariaLabel={`Customer actions for ${impactLabel}`}
                                                    onClick={(event) => event.stopPropagation()}
                                                    onKeyDown={(event) => event.stopPropagation()}
                                                    iconProps={{ iconName: 'More' }}
                                                    menuProps={{
                                                        items: [
                                                            ...(canEdit
                                                                ? [
                                                                      {
                                                                          key: 'edit',
                                                                          text: 'Edit',
                                                                          iconProps: { iconName: 'Edit' },
                                                                          onClick: () => onAddOrEditCustomer?.(item)
                                                                      }
                                                                  ]
                                                                : []),
                                                            {
                                                                key: 'remove',
                                                                text: 'Remove customer',
                                                                iconProps: { iconName: 'Delete' },
                                                                className: 'ci-kebab-remove',
                                                                onClick: () => handleRemoveCustomerImpact(item)
                                                            }
                                                        ]
                                                    }}
                                                    onRenderMenuIcon={() => null}
                                                />
                                            )}
                                            <IconButton
                                                className="ci-card-chevron"
                                                data-testid="customer-toggle-button"
                                                ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${impactLabel} details`}
                                                aria-expanded={expanded}
                                                aria-controls={`ci-card-body-${index}`}
                                                iconProps={{ iconName: expanded ? 'ChevronUp' : 'ChevronDown' }}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleCard(index);
                                                }}
                                                onKeyDown={(event) => event.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    {expanded && (
                                        <div className="ci-card-body" id={`ci-card-body-${index}`}>
                                            <div className="ci-section">
                                                <div className="ci-section-title">
                                                    <Trans>Customer details</Trans>
                                                </div>
                                                <div className="ci-grid">
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Customer name</Trans>
                                                        </span>
                                                        <span className="ci-value">{item.customerName || '--'}</span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>TPID</Trans>
                                                        </span>
                                                        <span className="ci-value">{item.tpId || '--'}</span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Country</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {item.customerCountry?.name || '--'}
                                                        </span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Status</Trans>
                                                        </span>
                                                        <span className="ci-value">{item.status || '--'}</span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Zip</Trans>
                                                        </span>
                                                        {item.zipCode ? (
                                                            <TooltipHost
                                                                directionalHint={DirectionalHint.topCenter}
                                                                content={getTooltip(item.city, item.state)}
                                                                id={`${tooltipId}-zip-${index}`}>
                                                                <span className="ci-value" style={{ cursor: 'pointer' }}>
                                                                    {item.zipCode}
                                                                </span>
                                                            </TooltipHost>
                                                        ) : item.city || item.state ? (
                                                            <TooltipHost
                                                                directionalHint={DirectionalHint.topCenter}
                                                                content={getTooltip(item.city, item.state)}
                                                                id={`${tooltipId}-citystate-${index}`}>
                                                                <span className="ci-value" style={{ cursor: 'pointer' }}>
                                                                    Unavailable
                                                                </span>
                                                            </TooltipHost>
                                                        ) : (
                                                            <span className="ci-value">--</span>
                                                        )}
                                                    </div>
                                                    {item.endCustomerSegment && (
                                                        <div className="ci-field">
                                                            <span className="ci-label">
                                                                <Trans>End customer</Trans>
                                                            </span>
                                                            <span className="ci-value">{item.endCustomerSegment}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="ci-section">
                                                <div className="ci-section-title">
                                                    <Trans>Impact data</Trans>
                                                </div>
                                                <div className="ci-grid">
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>MSX revenue</Trans>
                                                        </span>
                                                        <span className="ci-value">$ {formatCurrency(item.msxRevenue)}</span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>MSX device</Trans>
                                                        </span>
                                                        <span className="ci-value">{formatCurrency(item.msxDeviceCount)}</span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Seller-attested revenue</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {item.revenue ? '$ ' + formatCurrency(item.revenue) : '--'}
                                                        </span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Seller-attested device</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {item.deviceCount ? formatCurrency(item.deviceCount) : '--'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="ci-section">
                                                <div className="ci-section-title">
                                                    <Trans>Contact</Trans>
                                                </div>
                                                <div className="ci-grid">
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Contact name</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {companyInformation?.companyContactName || '--'}
                                                        </span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Contact number</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {getData(
                                                                companyInformation?.contactPhoneNumber ?? '',
                                                                countyCodePrefix.contactNumber
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Email</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {companyInformation?.contactEmailAddress || '--'}
                                                        </span>
                                                    </div>
                                                    <div className="ci-field">
                                                        <span className="ci-label">
                                                            <Trans>Added on</Trans>
                                                        </span>
                                                        <span className="ci-value">
                                                            {item.addedOn
                                                                ? ISOToDateTime(item.addedOn?.toString(), 'MMM DD YYYY')
                                                                : '--'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            {item.requestDescription && (
                                                <div className="ci-section">
                                                    <div className="ci-section-title"><Trans>Customer request description</Trans></div>
                                                    <div className="ci-impact-description">{getDescriptionText(item.requestDescription)}</div>
                                                </div>
                                            )}
                                            {item.cssCaseNumber && (
                                                <div className="ci-section">
                                                    <div className="ci-section-title"><Trans>CSS Case Number</Trans></div>
                                                    <Text>{item.cssCaseNumber}</Text>
                                                </div>
                                            )}
                                            {(() => {
                                                const parsedDescription = parseImpactDescription(
                                                    item.customerImpactDescription
                                                );
                                                const why = parseWhyImportant(item.customerImpactDescription);
                                                return (
                                                    <>
                                                        {why && (
                                                            <div className="ci-section">
                                                                <div className="ci-section-title">
                                                                    <Trans>Why this customer is important?</Trans>
                                                                </div>
                                                                <div className="ci-why-label">{why.label}</div>
                                                                {why.description && (
                                                                    <div className="ci-why-desc">{why.description}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {parsedDescription.customerImpact && (
                                                            <div className="ci-section">
                                                                <div className="ci-section-title">
                                                                    <Trans>Customer impact description</Trans>
                                                                </div>
                                                                {canManage && item.editId && item.editedOn && (
                                                                    <Link
                                                                        href={`/visual-flow/${encodeURIComponent(selectedRequest.unifiedTrackingId)}#customer-impact-edit-${item.editId}`}>
                                                                        <Trans>Edited</Trans>{' '}
                                                                        {ISOToDateTime(item.editedOn.toString(), 'MMM DD YYYY')}{' '}
                                                                        {item.editedBy}
                                                                    </Link>
                                                                )}
                                                                <div className="ci-impact-description">
                                                                    {parsedDescription.customerImpact}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
        </>
    );
};

export default CustomerDetailsTab;
